import { useCallback } from 'react';
import classNames from 'classnames';
import ReactPaginate, { ReactPaginateProps } from 'react-paginate';
import { isMobileClient } from '../../../utils/isMobileClient';
import { useNavigate } from 'react-router';

export type PaginationProps = Pick<ReactPaginateProps, 'onPageChange' | 'pageCount' | 'forcePage'>;

/**
 * The design system's pager — numbered pages, ‹ › steppers and an ellipsis break —
 * with no opinion about where the page number is kept.
 *
 * Split out of `Pagination` below, which puts the page in the URL. That is right for
 * a list that is the point of its route and should be linkable and back-button-able,
 * and wrong for one of several charts on a page: a chart's page is a local view state,
 * and `Pagination`'s navigation is hardcoded down to an `#article-filters` fragment.
 * Reaching for this directly needs no router in the tree, which is what lets a chart
 * page itself.
 */
export function PaginationControl({ pageCount, forcePage, onPageChange }: PaginationProps) {
  const isMobile = isMobileClient();

  return (
    <ReactPaginate
      className={classNames('react-paginate', 'pagination')}
      activeClassName="pagination--active"
      pageClassName="pagination__page"
      nextClassName="pagination__next"
      previousClassName="pagination__previous"
      pageLinkClassName="small-text"
      nextLinkClassName="small-text"
      previousLinkClassName="small-text"
      breakLinkClassName="small-text"
      breakLabel="..."
      nextLabel={<span aria-label="Next page">›</span>}
      forcePage={forcePage}
      onPageChange={onPageChange}
      pageCount={pageCount}
      previousLabel={<span aria-label="Previous page">‹</span>}
      disableInitialCallback
      pageRangeDisplayed={isMobile ? 1 : 2}
      marginPagesDisplayed={isMobile ? 1 : 2}
      ariaLabelBuilder={(page: number, selectedPage: number) =>
        page === selectedPage ? `Page ${page}, current page` : `Go to page ${page}`
      }
    />
  );
}

/**
 * The URL-driven pager: the page lives in `?offset=`, so it survives a reload and a
 * back button. `onPageChange` is accepted by the type but deliberately unused — this
 * variant owns where the page goes; pass a handler to `PaginationControl` instead.
 */
function Pagination(props: PaginationProps) {
  const navigate = useNavigate();

  const changePage = useCallback(
    ({ selected }: { selected: number }) => {
      //selected is 0-based, but offset is 1-based
      const nextPage = selected + 1;

      const currentPath = window.location.pathname;
      const searchParams = new URLSearchParams(window.location.search);

      searchParams.set('offset', nextPage.toString());

      const newUrl = `${currentPath}?${searchParams.toString()}#article-filters`;

      navigate(newUrl, {
        preventScrollReset: true,
        replace: true,
        flushSync: true,
      });
    },
    [navigate]
  );

  return <PaginationControl pageCount={props.pageCount} forcePage={props.forcePage} onPageChange={changePage} />;
}

export default Pagination;
